<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-between align-center mt-4">
      <v-container class="onest-font pl-0">
        <h1 class="font-bold text-5xl">Formulários de Validação</h1>
      </v-container>
      <v-container class="onest-font d-flex justify-end gap-2 pr-0">
        <v-btn
          @click="criarNovoFormulario"
          variant="flat"
          prepend-icon="mdi-plus"
          color="primary"
        >
          Criar novo formulário
        </v-btn>
      </v-container>
    </div>

    <v-container>
      <div class="search-bar w-full pb-4">
        <v-text-field
          v-model="searchQuery"
          variant="solo"
          flat
          hide-details
          class="search-input"
          placeholder="Pesquisar formulário por título..."
          aria-label="Pesquisar formulário por título..."
          append-inner-icon="mdi-magnify"
        ></v-text-field>
      </div>

      <!-- Menu de Filtros por Ativo/Inativo -->
      <div class="status-filters w-full pb-4 onest-font">
        <v-chip-group
          v-model="filtroAtivo"
          column
          mandatory
          selected-class="bg-primary text-white"
        >
          <v-chip value="TODOS" variant="outlined" class="font-normal">
            Todos
          </v-chip>
          <v-chip value="ATIVO" variant="outlined" class="font-normal">
            Ativos
          </v-chip>
          <v-chip value="INATIVO" variant="outlined" class="font-normal">
            Inativos
          </v-chip>
        </v-chip-group>
      </div>

      <div
        v-if="carregando"
        class="py-12 text-center text-gray-500 font-medium"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <p>Carregando formulários...</p>
      </div>

      <div
        v-else-if="formularios.length === 0"
        class="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm mt-4"
      >
        <div class="text-6xl mb-4">📄</div>
        <p class="text-lg">Nenhum formulário de validação cadastrado.</p>
      </div>

      <v-row v-else>
        <v-col>
          <div class="my-4">
            <table class="w-full border-separate border-spacing-y-2">
              <thead class="p-2">
                <tr>
                  <th class="p-4 font-medium text-left">Título</th>
                  <th class="p-4 font-medium text-left">Data de Definição</th>
                  <th class="p-4 font-medium text-left">Status</th>
                  <th class="p-4 font-medium text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in formulariosFiltrados"
                  :key="item.id"
                  class="hover:bg-blue-50/50 transition-colors bg-white shadow-sm"
                >
                  <td class="py-4 px-6 font-normal text-gray-600 rounded-l-xl">
                    {{ item.titulo }}
                  </td>
                  <td class="py-4 px-6 text-gray-600">
                    {{ formatarData(item.dataDefinicao) }}
                  </td>
                  <td class="py-4 px-2">
                    <v-chip
                      :color="item.ativo ? '#10B981' : '#EF4444'"
                      variant="tonal"
                      class="font-normal"
                      size="small"
                    >
                      {{ item.ativo ? 'Ativo' : 'Inativo' }}
                    </v-chip>
                  </td>
                  <td class="py-4 px-2 rounded-r-xl">
                    <v-tooltip location="bottom" text="Visualizar">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          @click="
                            $router.push(`/formulario-validacao/${item.id}`)
                          "
                          color="grey-darken-1"
                          variant="text"
                          size="small"
                          class="text-center font-normal rounded-full mr-2"
                        >
                          <v-icon>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                    <v-tooltip location="bottom" text="Editar">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          @click="editarFormulario(item)"
                          color="primary"
                          variant="text"
                          size="small"
                          class="text-center font-normal rounded-full mr-2"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                    <v-tooltip location="bottom" text="Excluir">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          @click="confirmarExclusao(item)"
                          color="error"
                          variant="text"
                          size="small"
                          class="text-center font-normal rounded-full"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </td>
                </tr>
                <tr v-show="formulariosFiltrados.length === 0">
                  <td
                    colspan="4"
                    class="py-8 text-center text-gray-500 bg-white rounded-xl shadow-sm"
                  >
                    Nenhum formulário encontrado para a busca "{{
                      searchQuery
                    }}".
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Modal de Confirmação de Exclusão -->
    <v-dialog v-model="dialogExcluir" max-width="500px">
      <v-card class="onest-font p-4">
        <v-card-title class="text-h5 font-bold"
          >Confirmar Exclusão</v-card-title
        >
        <v-card-text>
          Tem certeza de que deseja excluir o formulário "<strong>{{
            formularioSelecionado?.titulo
          }}</strong
          >"? Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions class="d-flex justify-end gap-2 mt-4">
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="dialogExcluir = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="excluirFormulario"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top right"
    >
      {{ snackbar.text }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  name: 'TelaManterFormularios',
  data() {
    return {
      searchQuery: '',
      filtroAtivo: 'TODOS',
      dialogCriarEditar: false,
      dialogExcluir: false,
      formularioSelecionado: null,
      deleting: false,
      formularioModel: {
        id: null,
        titulo: '',
        dataDefinicao: '',
        ativo: true,
      },
      formularios: [],
      carregando: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
    };
  },
  computed: {
    listaVazia() {
      return this.formularios.length === 0;
    },
    formulariosFiltrados() {
      let result = this.formularios;

      // 1. Filtrar por query de busca
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter((f) => f.titulo.toLowerCase().includes(query));
      }

      // 2. Filtrar por status ativo/inativo
      if (this.filtroAtivo === 'ATIVO') {
        result = result.filter((f) => f.ativo === true);
      } else if (this.filtroAtivo === 'INATIVO') {
        result = result.filter((f) => f.ativo === false);
      }

      return result;
    },
  },
  mounted() {
    this.carregarFormularios();
  },
  methods: {
    async carregarFormularios() {
      this.carregando = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/v1/formularioValidacao`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        this.formularios = response.data.map((f) => ({
          id: f.id_formulario_validacao,
          titulo: f.titulo,
          dataDefinicao: f.data_definicao ? f.data_definicao.slice(0, 10) : '',
          ativo: f.fl_ativo !== undefined ? f.fl_ativo : true,
        }));
      } catch (error) {
        console.error('Erro ao carregar formulários:', error);
      } finally {
        this.carregando = false;
      }
    },
    criarNovoFormulario() {
      this.$router.push('/formulario-validacao/novo');
    },
    abrirModalCriar() {
      this.$router.push('/formulario-validacao/novo');
    },
    editarFormulario(item) {
      this.$router.push(`/formulario-validacao/${item.id}?mode=edit`);
    },
    confirmarExclusao(item) {
      this.formularioSelecionado = item;
      this.dialogExcluir = true;
    },
    salvarFormulario() {
      this.dialogCriarEditar = false;
    },
    async excluirFormulario() {
      if (!this.formularioSelecionado) {
        return;
      }
      this.deleting = true;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${API_URL}/api/v1/formularioValidacao/${this.formularioSelecionado.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        this.formularios = this.formularios.filter(
          (f) => f.id !== this.formularioSelecionado.id,
        );
        this.snackbar.text = 'Formulário excluído com sucesso.';
        this.snackbar.color = 'success';
        this.snackbar.show = true;
      } catch (error) {
        console.error('Erro ao excluir formulário:', error);
        const msg =
          error.response?.data?.error ||
          'Não foi possível excluir o formulário.';
        this.snackbar.text = msg;
        this.snackbar.color = 'error';
        this.snackbar.show = true;
      } finally {
        this.dialogExcluir = false;
        this.formularioSelecionado = null;
        this.deleting = false;
      }
    },
    formatarData(dataString) {
      if (!dataString) {
        return '';
      }
      const parts = dataString.split('T')[0].split('-');
      if (parts.length === 3) {
        const [ano, mes, dia] = parts;

        return `${dia}/${mes}/${ano}`;
      }

      return dataString;
    },
  },
};
</script>

<style scoped>
/* Estilos foram removidos porque agora usamos TailwindCSS e Vuetify no <template> */
</style>
